import React, { useCallback, useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import clsx from 'clsx';

import Tr from '../../../components/common-table/components/Tr';
import Td from '../../../components/common-table/components/Td';
import Icon from '../../../icons/Icon';
import { ICONS } from '../../../icons';
import IsGranted from '../../../components/is-granted/IsGranted';
import { PROJECT_TYPES, USER_ROLES } from '../../../constants';
import CommonIconButton from '../../../components/common-icon-button/CommonIconButton';
import CommonTooltip from '../../../components/common-tooltip/CommonTooltip';

import scss from '../ProjectsList.module.scss';

const ProjectsTableItem = ({
  project,
  projectTypeOptions,
  onOpen,
  onShare,
  onDelete,
  onDeactivate,
  onEdit,
  onCollectData,
  disabled = false,
}) => {
  const {
    id,
    projectName,
    active,
    createdBy,
    createdOn,
    dataSets = 20,
    projectType,
    activeSharing,
    collectedFrom,
    collectedTill,
  } = project || {};

  const { t } = useTranslation('projects');

  const projectTypeData = useMemo(
    () => projectTypeOptions.find((op) => op.value === projectType) || {},
    [projectType, projectTypeOptions],
  );

  const disableText = useMemo(() => {
    if (projectType === PROJECT_TYPES.SONAR && !collectedFrom && !collectedTill) {
      return t('collectDataToOpenTheProject');
    }

    return '';
  }, [t, projectType, collectedFrom, collectedTill]);

  const handleOpenProject = useCallback(() => {
    if (onOpen && active && !disabled && !disableText) {
      onOpen(project);
    }
  }, [project, onOpen, active, disableText, disabled]);

  const handleShareProject = useCallback(() => {
    if (onShare) {
      onShare(project);
    }
  }, [project, onShare]);

  const handleCollectData = useCallback(() => {
    if (onCollectData) {
      onCollectData(id);
    }
  }, [id, onCollectData]);

  const handleEditProject = useCallback(() => {
    if (onEdit) {
      onEdit(project);
    }
  }, [project, onEdit]);

  const handleDeleteProject = useCallback(() => {
    if (onDelete) {
      onDelete(project);
    }
  }, [project, onDelete]);

  const handleDeactivateProject = useCallback(() => {
    if (onDeactivate) {
      onDeactivate(project);
    }
  }, [project, onDeactivate]);

  return (
    <Tr extraClasses={clsx((disabled || !active) && scss.rowBlocked)} disableHover={disabled || !active}>
      <Td
        extraClasses={clsx(
          scss.cellProjectWrapper,
          scss.cellLeft,
          active && !disableText && !disabled && scss.cellPointer,
          (disabled || disableText) && scss.cellNotAllowed,
        )}
        onClick={handleOpenProject}>
        <CommonTooltip title={disableText}>
          <div className={scss.cellProject}>
            <Icon icon={projectTypeData?.icon ?? ICONS.Logo} disableCommonColors />
            <span>{projectTypeData?.label ?? t('undefined')}</span>
          </div>
        </CommonTooltip>

        {projectType === PROJECT_TYPES.SONAR && !collectedFrom && !collectedTill ? (
          <div className={clsx(scss.tableMarker, scss.tableMarker_red)}>
            <span>{t('noDataCollected')}</span>
          </div>
        ) : null}
      </Td>

      <Td
        extraClasses={clsx(
          scss.cellLeft,
          active && !disableText && !disabled && scss.cellPointer,
          (disabled || disableText) && scss.cellNotAllowed,
        )}
        onClick={handleOpenProject}>
        <CommonTooltip title={disableText}>
          <div>{projectName}</div>
        </CommonTooltip>
      </Td>
      <Td extraClasses={clsx(scss.cellLeft, scss.cell100)}>{dataSets}</Td>
      <Td extraClasses={scss.cellLeft}>{createdOn}</Td>
      <Td extraClasses={scss.cellLeft}>{createdBy}</Td>
      <Td extraClasses={clsx(scss.cellLeft, scss.cell100)}>{active ? t('active') : t('inactive')}</Td>
      <IsGranted permissions={USER_ROLES.GLOBAL_ADMIN}>
        <Td extraClasses={scss.cellActions}>
          {activeSharing ? (
            <CommonIconButton onClick={handleShareProject} icon={ICONS.Share} tooltip={t('share')} />
          ) : null}

          <CommonIconButton
            onClick={handleDeactivateProject}
            icon={active ? ICONS.EyeCrossed : ICONS.Eye}
            tooltip={active ? t('deactivate') : t('activate')}
            isStrokeIcon
          />

          {projectType === PROJECT_TYPES.SONAR ? (
            <CommonIconButton onClick={handleCollectData} icon={ICONS.Unclust} tooltip={t('collectData')} />
          ) : null}

          <CommonIconButton onClick={handleEditProject} icon={ICONS.Edit} tooltip={t('edit')} />

          <CommonIconButton onClick={handleDeleteProject} icon={ICONS.Delete} tooltip={t('delete')} />
        </Td>
      </IsGranted>
    </Tr>
  );
};

export default ProjectsTableItem;
